import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Link as LinkTo } from "react-router";
import { ObrasTopBar } from "../../components/obras_top_bar";
import { useObrasChecklistareaViewModel } from "../../viewModels/obras_checklistarea_view_model";
import { ObrasTopHeader } from "../../components/obras_top_header";
import { Header } from "./components/header";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { useMemo } from "react";
import { GenerateObraAreaChecklistItemsColumnsDef } from "./columns";
import { CustomStyledDataGrid } from "./components/custom_styled_data_grid";
import { GRID_CHECKBOX_SELECTION_FIELD, GridPinnedColumnFields } from "@mui/x-data-grid-pro";
import { LegendTagIcon } from "../../../../../common/components/legend_tag_icon";
import { LegendTagItemComponent } from "../../../../../common/components/legend_tag_item_component";
import { ObraChecklistServico } from "../../../domain/models/obra_checklist_servico";
import { ObraChecklistServicoModal } from "./components/obra_checklist_servico_modal";
import { ObrasReportModals } from "../../components/obras_report_modals";
import { ObraChecklistServicoLiberacaoModal } from "./components/obra_checklist_servico_liberacao_modal";

import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import BottomBar from "../../../../../common/components/bottom_bar";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * ObrasChecklistarea
 */

export const ObrasChecklistarea = () => {
  const viewModel = useObrasChecklistareaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateObraAreaChecklistItemsColumnsDef({
      obraAreaChecklistStatus: viewModel.obraAreaChecklistHeader !== null ? viewModel.obraAreaChecklistHeader.status : null,
      onEnterChecklists: viewModel.handleEnterInspecoes,
      onActionDeletar: viewModel.handleDeleteObraChecklistServicoOpenState,
      onLiberarChecklists: viewModel.handleLiberarChecklistOpenState,
    });
  }, [
    viewModel.obraAreaChecklistHeader,
    viewModel.handleEnterInspecoes,
    viewModel.handleDeleteObraChecklistServicoOpenState,
    viewModel.handleLiberarChecklistOpenState,
  ]);

  const pinnedColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.obraChecklistServico.length > 0 ?
      {
        left: [
          GRID_CHECKBOX_SELECTION_FIELD,
        ],
        right: [
          'idObraChecklistServico',
        ],
      } : {
      };
  }, [
    viewModel.obraChecklistServico,
  ]);

  return (
    <>
      {/**
        * ObrasTopBar
        */}

      <ObrasTopBar
        obrasReportState={viewModel.obrasReportState}
        state={viewModel.obrasTopHeaderState}
        selectedObra={viewModel.selectedObraHeaderInfo ?? null}
        leftBackSlotButton={
          <>
            {
              (viewModel.obraAreaChecklistHeader && viewModel.obraAreaChecklistHeader.status === 0) &&
              <div className="w-[7rem]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    viewModel.handleChecklistServicoOpenState(null, true)
                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>
            }
          </>
        }
        onBack={viewModel.handleBack}>
      </ObrasTopBar>

      <div className="flex w-full gap-2 flex-col py-4 px-4 mb-24">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Obras
          </Link>
          <Link underline="hover" color="inherit" component={'span'}>
            <LinkTo to='/Obras_lista'>Lista de Obras</LinkTo>
          </Link>
          <Link underline="hover" color="inherit" component={'span'}>
            <LinkTo to={`/Obras_cadastroarea?id=${viewModel.idObra}`}>Checklist de Área</LinkTo>
          </Link>
          <Typography className="text-primary-green">
            Checklist de Serviço
          </Typography>
        </Breadcrumbs>

        {/**
          * ObrasTopHeader
          */}

        <ObrasTopHeader
          title="Checklist de Serviço"
          selectedObra={viewModel.selectedObraHeaderInfo ?? null}
          additionalHeader={
            viewModel.obraAreaChecklistHeader && (
              <div className="flex flex-row text-black dark:text-white">
                <span className="font-extrabold text-xl mr-2">Área: </span>
                <span className="mx-1 font-base text-base">{viewModel.obraAreaChecklistHeader.descricao}</span>
              </div>
            )
          }
          onReload={viewModel.handleReload}
        />

        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload}
                />
              </div>
            ) : (
              <>
                <Header
                  statusChecklist={viewModel.filters.statusChecklist}
                  statusVerificacao={viewModel.filters.statusVerificacao}
                  onChangeStatusChecklist={viewModel.handleStatusChecklistChange}
                  onChangeStatusVerificacao={viewModel.handleStatusVerificacaoChange}
                  onClearFilter={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <div className="w-full flex flex-row justify-between items-end">
                    <div className="flex flex-col flex-wrap">
                      <LegendComponent>
                        <LegendTagItemComponent
                          icon={<LegendTagIcon title='I.A' bgColor="#26C281" />}
                          label='I. Aprovação(%)' />

                        <LegendTagItemComponent
                          icon={<LegendTagIcon title='I.R' bgColor="yellow" />}
                          label='I. Retrabalho(%)' />

                        <LegendTagItemComponent
                          icon={<LegendTagIcon title='I.RA' bgColor="#FFB848" />}
                          label='I. Retrabalho Aprovado(%)' />

                        <LegendTagItemComponent
                          icon={<LegendTagIcon title='I.REP' bgColor="#EF4836" />}
                          label='I. Reprovação(%)' />

                      </LegendComponent>
                    </div>

                    <div className="flex flex-col justify-between flex-1">
                      <TablePagination
                        component="div"
                        count={viewModel.pagination.totalItems}
                        page={viewModel.pagination.page - 1}
                        onPageChange={(_, p) => viewModel.handlePageChange(p)}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        rowsPerPage={viewModel.pagination.rowsPerPage}
                        onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />
                    </div>
                  </div>

                  <CustomStyledDataGrid
                    tableName="obras_checklistarea"
                    disableColumnMenu={false}
                    getRowId={(row: ObraChecklistServico) => row.idObraChecklistServico}
                    getRowHeight={() => 'auto'}
                    textSize="pequeno"
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    rows={viewModel.obraChecklistServico}
                    rowCount={viewModel.obraChecklistServico.length}
                    pinnedColumns={pinnedColumns}
                    checkboxSelection={true}
                    disableRowSelectionOnClick={true}
                    rowSelectionModel={viewModel.rowSelectionModel}
                    onRowSelectionModelChange={viewModel.handleRowSelectionModelChange}
                    disableColumnSorting={false}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />

                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))} />

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * BottomBar
        */}

      <BottomBar>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
          </div>

          <div className="flex flex-row gap-4">
            {
              (viewModel.obraAreaChecklistHeader && viewModel.obraAreaChecklistHeader.status === 0) &&
              <div className="w-[210px]">
                <PrimaryButton
                  buttonColor="red"
                  onClick={() => viewModel.handleDeleteLoteObraChecklistServicoOpenState(true)}
                  startIcon={<DynamicIcon nameIcon="DeleteForever" size={25} className="text-white" />}>
                  Excluir selecionados
                </PrimaryButton>
              </div>
            }
          </div>
        </div>
      </BottomBar>

      {/**
        * Novo ObraChecklistServico Modal
        */}

      <ObraChecklistServicoModal
        open={viewModel.obraChecklistServicoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        checklistServicos={viewModel.filters.checklistServicoDropdown ?? []}
        onSelectTipoServico={viewModel.handleSelectTipoServico}
        onClose={() => viewModel.handleChecklistServicoOpenState(null, false)}
        onSubmit={viewModel.handleSubmitChecklistServicos}
      />

      <ObraChecklistServicoLiberacaoModal
        open={viewModel.obraChecklistServicoLiberacaoModal}
        saving={viewModel.saving}
        loading={viewModel.loadingLiberacao}
        readOnly={viewModel.obraAreaChecklistHeader?.status !== 0}
        liberacao={viewModel.obraChecklistServicoLiberacao}
        selectedObra={viewModel.selectedObraHeaderInfo ?? null}
        areaDescricao={viewModel.obraAreaChecklistHeader?.descricao ?? null}
        checklistDescricao={viewModel.checklistServicoDescription}
        funcionariosDropdown={viewModel.funcionariosDropdown}
        onClose={() => viewModel.handleLiberarChecklistOpenState(null, null, false)}
        onSubmit={viewModel.handleSubmitChecklistServicosLiberacao}
      />

      <DeleteDialog
        open={viewModel.obraChecklistServicoDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir o item selecionado?'
        onClose={() => viewModel.handleDeleteObraChecklistServicoOpenState(null, false)}
        onDelete={() => viewModel.handleDeleteObraChecklistServicoConfirm()}
      />

      <DeleteDialog
        open={viewModel.obraChecklistServicoDeleteLoteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir os itens selecionados?'
        onClose={() => viewModel.handleDeleteLoteObraChecklistServicoOpenState(false)}
        onDelete={() => viewModel.handleDeleteLoteObraChecklistServicoConfirm()}
      />

      {/**
        * Relatorios obras.
        */}

      <ObrasReportModals
        state={viewModel.obrasReportState}
      />

    </>
  );
}
